import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAppContext } from './AppContext';
import ViewContext from './ViewContext';
import { useAuth } from '../auth/AuthContext';
import { energyService } from '../api/energyApi';
import { buildEnergyViewModel, extractEnergySiteIds } from '../utils/energyViewModel';
import { EnergySync } from '../services/energy/energySync';
import { LiveEnergySync, mergeLiveEnergy } from '../services/energy/liveEnergy';
import { createCalculationWorker, createInlineCalculator } from '../services/energy/calculationWorker';

export { collectEnergyPages } from '../services/energy/energySync';
const EnergyContext = createContext(null);
const ENERGY_VIEWS = new Set(['dashboard', 'bessView', 'emsView', 'loadView', 'pccView', 'energyReports', 'canopyView', 'fleetView']);
const empty = () => ({ raw: {}, model: buildEnergyViewModel({}) });

export const EnergyProvider = ({ children }) => {
  const auth = useAuth();
  const { customerContext, fleetContext } = useAppContext();
  const view = useContext(ViewContext);
  const customerId = auth.isSuperuser ? customerContext?.customerId || auth.customerId || null : auth.customerId || customerContext?.customerId || null;
  const fleetId = fleetContext?.fleetId || null;
  const scopeKey = `${customerId || ''}:${fleetId || ''}`;
  const active = !view?.currentView || ENERGY_VIEWS.has(view.currentView);
  const [range, setRange] = useState('day');
  const [scenario, setScenario] = useState('standard');
  const [calculated, setCalculated] = useState(empty);
  const [liveData, setLiveData] = useState(null);
  const [liveStatus, setLiveStatus] = useState({ loading: true, errors: [] });
  const [status, setStatus] = useState({ loading: false, refreshing: false, errors: [] });
  const [discovery, setDiscovery] = useState({ key: null, sites: [], inventory: [], siteId: null });
  const [cursors, setCursors] = useState({ confirmedCommitSeq: null, highestReceivedCommitSeq: '0' });
  const [lastSuccessfulAt, setLastSuccessfulAt] = useState(null);
  const session = useRef(null);
  const liveSession = useRef(null);
  const discoveryAbort = useRef(null);

  useEffect(() => {
    setCalculated(empty()); setCursors({ confirmedCommitSeq: null, highestReceivedCommitSeq: '0' });
    setDiscovery({ key: null, sites: [], inventory: [], siteId: null }); setScenario('standard');
  }, [scopeKey]);

  useEffect(() => {
    if (!active || !customerId || !auth.isAuthenticated) return undefined;
    const controller = new AbortController(); discoveryAbort.current = controller;
    setStatus({ loading: true, refreshing: false, errors: [] });
    (async () => {
      const [latest, inventory] = await Promise.all([
        energyService.getLatest({ customerId, ...(fleetId ? { fleetId } : {}) }, { signal: controller.signal }),
        fleetId ? energyService.getFleetCanopies(fleetId, { customerId }, { signal: controller.signal }) : Promise.resolve(null),
      ]);
      if (controller.signal.aborted) return;
      if (!latest?.success || (inventory && !inventory.success)) throw new Error(latest?.message || inventory?.message || 'Energy scope could not be loaded.');
      const rows = inventory?.data?.data || [];
      const sites = [...new Set([...extractEnergySiteIds(latest.data), ...rows.map(row => row.siteId).filter(Boolean)])].sort();
      setDiscovery(previous => ({ key: scopeKey, inventory: rows, sites,
        siteId: previous.key === scopeKey && sites.includes(previous.siteId) ? previous.siteId : sites.length === 1 ? sites[0] : null }));
      if (sites.length !== 1) setStatus({ loading: false, refreshing: false, errors: [{ source: 'site selection', message: sites.length ? 'Select an energy site to continue.' : 'No energy site is available for this account.' }] });
    })().catch(error => { if (!controller.signal.aborted) setStatus({ loading: false, refreshing: false, errors: [{ source: 'energy', message: error.message }] }); });
    return () => controller.abort();
  }, [active, customerId, fleetId, scopeKey, auth.isAuthenticated]);

  useEffect(() => {
    setLiveData(null);
    setLiveStatus({ loading: true, errors: [] });
    if (!active || !customerId || !auth.isAuthenticated || discovery.key !== scopeKey || !discovery.siteId) return undefined;
    let live = true;
    const sync = new LiveEnergySync({ api: energyService, token: auth.idToken,
      scope: { customerId, siteId: discovery.siteId, fleetId, canopyInventory: discovery.inventory },
      onUpdate: result => { if (live) setLiveData(result); },
      onStatus: change => { if (live) setLiveStatus(previous => ({ ...previous, ...change })); },
    });
    liveSession.current = sync;
    sync.start().catch(error => sync.report(error));
    return () => { live = false; sync.close(); if (liveSession.current === sync) liveSession.current = null; };
  }, [active, customerId, fleetId, scopeKey, discovery, auth.idToken, auth.isAuthenticated]);

  useEffect(() => {
    if (!active || !customerId || !auth.isAuthenticated || discovery.key !== scopeKey || !discovery.siteId) return undefined;
    let live = true;
    // Reuse only the whole current snapshot while a newly selected period loads.
    setCalculated(previous => previous.model.meta.siteId === discovery.siteId ? {
      raw: { latest: previous.raw.latest },
      model: { ...previous.model, energy: empty().model.energy, series: [], rollups: [], history: {},
        measures: { ...previous.model.measures, energy: {}, costs: { current: previous.model.measures?.costs?.current, summary: {} } },
        costs: { ...empty().model.costs, currency: previous.model.costs.currency, utilityRatePerHour: previous.model.costs.utilityRatePerHour,
          modeledGenerationRatePerHour: previous.model.costs.modeledGenerationRatePerHour,
          allocatedLeaseRatePerHour: previous.model.costs.allocatedLeaseRatePerHour } },
    } : empty());
    const calculator = typeof Worker === 'function' ? createCalculationWorker() : createInlineCalculator();
    const sync = new EnergySync({ api: energyService, token: auth.idToken,
      scope: { customerId, siteId: discovery.siteId, fleetId, scenario, canopyInventory: discovery.inventory },
      range, calculator,
      onUpdate: (result, cursor) => {
        if (!live) return;
        setCalculated(result); setCursors(cursor); setLastSuccessfulAt(new Date().toISOString());
      },
      onStatus: change => { if (live) setStatus(previous => ({ ...previous, ...change })); },
    });
    session.current = sync;
    sync.start().catch(error => sync.report(error));
    return () => { live = false; sync.close(); if (session.current === sync) session.current = null; };
  }, [active, customerId, fleetId, scopeKey, discovery, range, scenario, auth.idToken, auth.isAuthenticated]);

  const refresh = useCallback(async () => {
    liveSession.current?.run(false).catch(() => {});
    if (session.current) return session.current.run(true).catch(() => {});
    // Retry discovery after an initial failure without restoring a polling loop.
    discoveryAbort.current?.abort();
    const controller = new AbortController(); discoveryAbort.current = controller;
    const latest = await energyService.getLatest({ customerId, ...(fleetId ? { fleetId } : {}) }, { signal: controller.signal });
    if (!controller.signal.aborted && latest?.success) {
      const sites = extractEnergySiteIds(latest.data);
      setDiscovery(previous => ({ ...previous, key: scopeKey, sites, siteId: sites.length === 1 ? sites[0] : null }));
    }
    return undefined;
  }, [customerId, fleetId, scopeKey]);
  const setSelectedSiteId = useCallback(siteId => setDiscovery(previous => previous.sites.includes(siteId) ? { ...previous, siteId } : previous), []);
  const value = useMemo(() => ({ ...mergeLiveEnergy(calculated, liveData), ...status, ...cursors,
    loading: !liveData && status.loading && liveStatus.loading,
    historyLoading: status.loading || status.refreshing,
    errors: [...liveStatus.errors, ...status.errors],
    liveCommitSeq: liveData?.model.meta.watermarkCommitSeq ?? null,
    range, setRange, scenario, setScenario, refresh,
    timezone: calculated.model.meta.timezone || customerContext?.billingTimezone || 'America/New_York',
    hasScope: Boolean(customerId), siteOptions: discovery.key === scopeKey ? discovery.sites : [],
    selectedSiteId: discovery.key === scopeKey ? discovery.siteId : null, setSelectedSiteId, lastSuccessfulAt,
    synchronization: 'commit-journal',
  }), [calculated, liveData, liveStatus, status, cursors, range, scenario, refresh, customerContext, customerId, discovery, scopeKey, setSelectedSiteId, lastSuccessfulAt]);
  return <EnergyContext.Provider value={value}>{children}</EnergyContext.Provider>;
};

export const useEnergy = () => {
  const context = useContext(EnergyContext);
  if (!context) throw new Error('useEnergy must be used within an EnergyProvider');
  return context;
};
export const useEnergyContext = () => useContext(EnergyContext);
export default EnergyContext;
